import { VALUE_CHAIN_STATUS } from 'constants/interfaces';
import { VALUE_CHAIN_MAP_LIST_TAG } from 'store/tags/valueChainMap';

import type IValueChainMap from 'store/slices/valueChainMap/interfaces/IValueChainMap';
import type IValueChainMapRequest from 'store/slices/valueChainMap/interfaces/IValueChainMapRequest';
import type IDeleteValueChainItemFromList from './interfaces/IDeleteValueChainItemFromList';
import type IUpdateValueChainMapList from './interfaces/IUpdateValueChainMapList';

export const getValueChainMapListWithItem = (
  items: IValueChainMap[],
  item: IValueChainMap,
): IValueChainMap[] => [
  item,
  ...items.filter((listItem) => listItem.id !== item.id),
];

const isValueChainMapItemVisibleInList = (
  item: IValueChainMap,
  originalArgs: IValueChainMapRequest,
  processingValuesChainMapId: number[],
): boolean => {
  const statusFilter = originalArgs.status;
  const isProcessingItem = item.status === VALUE_CHAIN_STATUS.processing
    && processingValuesChainMapId.includes(item.id);

  if (isProcessingItem) {
    return true;
  }

  if (!statusFilter || statusFilter.length === 0) {
    return true;
  }

  return statusFilter.includes(item.status);
};

export const updateValueChainMapList = ({
  valueChainMapApi, state, updatedData, dispatch, chainId,
}: IUpdateValueChainMapList) => {
  valueChainMapApi.util.selectInvalidatedBy(state, [VALUE_CHAIN_MAP_LIST_TAG])
    .forEach(({ endpointName, originalArgs }) => {
      if (endpointName === 'getValueChainMapList') {
        dispatch(valueChainMapApi.util.updateQueryData(
          endpointName,
          originalArgs,
          (cache) => {
            const parseCache = cache;
            const index = parseCache.data.items.findIndex((item) => item.id === +chainId);

            if (index !== undefined && index !== -1) {
              const { createdBy } = parseCache.data.items[index];
              parseCache.data.items[index] = { ...updatedData.data, createdBy };
            }

            return parseCache;
          },
        ));
      }
    });
};

export const upsertValueChainMapList = ({
  valueChainMapApi, state, updatedData, dispatch, chainId,
}: IUpdateValueChainMapList) => {
  valueChainMapApi.util.selectInvalidatedBy(state, [VALUE_CHAIN_MAP_LIST_TAG])
    .forEach(({ endpointName, originalArgs }) => {
      if (endpointName === 'getValueChainMapList') {
        dispatch(valueChainMapApi.util.updateQueryData(
          endpointName,
          originalArgs,
          (cache) => {
            const parseCache = cache;
            const index = parseCache.data.items.findIndex((item) => item.id === +chainId);
            const shouldKeepItem = isValueChainMapItemVisibleInList(
              updatedData.data,
              originalArgs,
              state.localStorage.processingValuesChainMapId,
            );

            if (!shouldKeepItem && index === -1) {
              return parseCache;
            }

            if (index !== -1) {
              const { createdBy } = parseCache.data.items[index];
              const cachedItems = [...parseCache.data.items];
              cachedItems.splice(index, 1);
              parseCache.data.items = getValueChainMapListWithItem(
                cachedItems,
                { ...updatedData.data, createdBy },
              );

              return parseCache;
            }

            parseCache.data.items = getValueChainMapListWithItem(parseCache.data.items, updatedData.data);
            parseCache.data.meta = {
              ...parseCache.data.meta,
              count: parseCache.data.meta.count + 1,
            };

            return parseCache;
          },
        ));
      }
    });
};

export const deleteValueChainItemFromList = ({
  valueChainMapApi, state, dispatch, chainId,
}: IDeleteValueChainItemFromList) => {
  valueChainMapApi.util.selectInvalidatedBy(state, [VALUE_CHAIN_MAP_LIST_TAG])
    .forEach(({ endpointName, originalArgs }) => {
      if (endpointName === 'getValueChainMapList') {
        dispatch(valueChainMapApi.util.updateQueryData(
          endpointName,
          originalArgs,
          (cache) => {
            const parseCache = cache;
            const index = parseCache.data.items.findIndex((item) => item.id === chainId);

            if (index !== undefined && index !== -1) {
              const cachedItems = [...parseCache.data.items];
              cachedItems.splice(index, 1);

              parseCache.data.items = cachedItems;
            }

            return parseCache;
          },
        ));
      }
    });
};
